/**
 * 推送消息 — GET/POST /api/admin/push
 *
 * 列表展示已发送的站内推送，点击"新建推送"打开撰写弹窗。
 * audience 为 broadcast 时忽略 user_ids，targeted 时必须提供非空 user_ids。
 */
import { useState } from 'react';
import {
  Button,
  Form,
  Input,
  Modal,
  Radio,
  Tag,
  message,
} from 'antd';
import { useQueryClient } from '@tanstack/react-query';
import { DataTable } from '../../components/DataTable';
import { api, ApiError } from '../../api/client';
import { useAuthStore } from '../../store/auth';

interface PushRow {
  id: number;
  title: string;
  body: string;
  audience: 'broadcast' | 'targeted';
  sent_by: number;
  sent_at: string;
  create_time: string;
  recipient_count: number;
}

interface PushForm {
  title: string;
  body: string;
  audience: 'broadcast' | 'targeted';
  user_ids?: string;
}

export function PushPage() {
  const qc = useQueryClient();
  const [composeOpen, setComposeOpen] = useState(false);
  const [form] = Form.useForm<PushForm>();
  const audience = Form.useWatch('audience', form);
  const hasNode = useAuthStore((s) => s.hasNode);
  const canSend = hasNode('platform.push.send');

  async function onSubmit(values: PushForm) {
    const payload: {
      title: string;
      body: string;
      audience: 'broadcast' | 'targeted';
      user_ids?: number[];
    } = {
      title: values.title,
      body: values.body,
      audience: values.audience,
    };
    if (values.audience === 'targeted') {
      const ids = (values.user_ids ?? '')
        .split(/[,\s]+/)
        .map((s) => s.trim())
        .filter(Boolean)
        .map((s) => Number(s));
      if (ids.length === 0 || ids.some((n) => !Number.isInteger(n) || n <= 0)) {
        message.error('定向推送需要提供有效的正整数用户 ID 列表');
        return;
      }
      payload.user_ids = ids;
    }
    try {
      await api.post('/api/admin/push', payload);
      message.success('推送已发送');
      setComposeOpen(false);
      form.resetFields();
      qc.invalidateQueries({ queryKey: ['admin', 'push'] });
    } catch (e) {
      message.error(e instanceof ApiError ? e.message : '发送失败');
    }
  }

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 70 },
    { title: '标题', dataIndex: 'title', ellipsis: true },
    {
      title: '受众',
      dataIndex: 'audience',
      width: 100,
      render: (v: 'broadcast' | 'targeted') =>
        v === 'broadcast' ? <Tag color="blue">全员</Tag> : <Tag color="purple">定向</Tag>,
    },
    {
      title: '接收人数',
      dataIndex: 'recipient_count',
      width: 110,
    },
    { title: '发送人', dataIndex: 'sent_by', width: 90 },
    { title: '发送时间', dataIndex: 'sent_at', width: 170 },
  ];

  return (
    <div>
      <h2 style={{ marginTop: 0 }}>推送消息</h2>
      <div style={{ marginBottom: 12 }}>
        {canSend && (
          <Button type="primary" onClick={() => setComposeOpen(true)}>
            新建推送
          </Button>
        )}
      </div>

      <DataTable<PushRow>
        queryKey={['admin', 'push']}
        endpoint="/api/admin/push"
        columns={columns}
        rowKey="id"
        scroll={{ x: 900 }}
      />

      <Modal
        open={composeOpen}
        title="撰写推送"
        onCancel={() => setComposeOpen(false)}
        onOk={() => form.submit()}
        okText="发送"
        width={640}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{ audience: 'broadcast' }}
          onFinish={onSubmit}
        >
          <Form.Item name="title" label="标题" rules={[{ required: true, max: 200 }]}>
            <Input />
          </Form.Item>
          <Form.Item
            name="body"
            label="正文"
            rules={[{ required: true, min: 1, max: 10000 }]}
          >
            <Input.TextArea rows={6} />
          </Form.Item>
          <Form.Item name="audience" label="受众" rules={[{ required: true }]}>
            <Radio.Group>
              <Radio value="broadcast">全员广播</Radio>
              <Radio value="targeted">定向用户</Radio>
            </Radio.Group>
          </Form.Item>
          {audience === 'targeted' && (
            <Form.Item
              name="user_ids"
              label="用户 ID 列表"
              rules={[{ required: true, message: '请输入至少一个用户 ID' }]}
              extra="逗号或空格分隔的用户 ID，例如 101, 102, 103"
            >
              <Input.TextArea rows={3} placeholder="101, 102, 103" />
            </Form.Item>
          )}
        </Form>
      </Modal>
    </div>
  );
}
